<template>
    <v-app-bar :elevation="2">
        <v-img
            class="mx-2 cursor-pointer"
            :src="logo"
            max-height="60"
            max-width="120"
            contain
            @click="goToHome"
        />
        <v-divider vertical />
        <v-tabs color="primary">
            <v-tab
                v-for="page in pageList"
                v-show="showRoute(page.meta)"
                :key="page.path"
                :to="page.path"
            >
                {{ page.meta.display || `NO-DISPLAY: ${page.name}` }}
            </v-tab>
        </v-tabs>
        <v-spacer />
        <v-autocomplete
            :model-value="corporation"
            :prepend-inner-icon="getCorporationMdiIcon(corporation)"
            :disabled="corporations.length<=1"
            :item-title="item => `${item.name} (${item.corporationNumber})`"
            item-value="uuid"
            return-object
            :items="sortedCorporations"
            width="600px"
            :menu-props="{ 'width': '600px' }"
            @update:model-value="switchCorporation"
        >
            <template #item="{ props, item }">
                <v-list-item
                    v-bind="props"
                    :prepend-icon="`${getCorporationMdiIcon(item?.raw)}`"
                    :title="item.title"
                />
            </template>
        </v-autocomplete>
        <v-autocomplete
            :model-value="schoolYear"
            :item-title="item=> `${item.name}`"
            item-value="uuid"
            return-object
            :items="sortedSchoolYears"
            max-width="150px"
            min-width="100px"
            class="pl-4"
            @update:model-value="switchSchoolYear"
        />
        <v-menu location="bottom" open-on-click>
            <template #activator="{ props }">
                <v-btn
                    v-bind="props"
                    prepend-icon="mdi-account"
                    class="mx-4"
                >
                    {{ userName }}
                </v-btn>
            </template>
            <v-list>
                <v-list-item v-if="useAccountConsole" @click="openAccountSettings">
                    Account verwalten
                </v-list-item>
                <v-list-item v-else @click="openUpdatePassword">
                    Passwort ändern
                </v-list-item>
                <v-list-item base-color="error" @click="logout">
                    Abmelden
                </v-list-item>
            </v-list>
        </v-menu>
    </v-app-bar>
</template>

<script setup>
import INJECTION_KEYS from '@injectionKeys'
import { computed, inject, watch, watchEffect } from 'vue'
import { useRouter } from 'vue-router'

import logo from '/logo.svg'
import ROUTES from '@routes'
import { useRoles } from '@/composables/useRoles'
import { useSchools } from '@/composables/useSchools'
import { useSchoolAuthorities } from '@/composables/useSchoolAuthorities'
import CORPORATION_SCOPE_TYPE from '@/constants/corporationScopeType'
import { useCurrentUser } from '@/composables/useCurrentUser'
import { useCorporationScope } from '@/composables/useCorporationScope'
import CORPORATION_TYPE from '@/constants/corporationType'
import { useRoute } from 'vuetify/lib/composables/router'
import { useSchoolYears } from '@/composables/useSchoolYears'
import { useSortedList } from '@/composables/useSortedList'

/** @type {Session} */
const session = inject(INJECTION_KEYS.SESSIONSTATE)

/** @type {SnackState} */
const snackState = inject(INJECTION_KEYS.SNACKSTATE)

/** @type {GlobalState} */
const globalState = inject(INJECTION_KEYS.GLOBALSTATE)

const useAccountConsole = computed(() => {
    return globalState.systemConfig.useAccountManagementConsole
})

const router = useRouter()
const { sortedSchoolYears, schoolYears } = useSchoolYears()
const { userSchools, errorMessage: errorMessageSchools } = useSchools()
const { userSchoolAuthorities, errorMessage: errorMessageSchoolAuthorities } = useSchoolAuthorities()
const { corporationScopes, errorMessage: currentUserErrorMessage, userName, logout, openUpdatePassword, openAccountSettings } = useCurrentUser()
const { hasAdminRole } = useRoles()
const { corporationScope } = useCorporationScope()

const route = useRoute()
const { hasRole } = useRoles()


//in which order the routes appear in the nav bar
const pageOrder = [
    ROUTES.HOME,
    ROUTES.APPLICATIONS,
    ROUTES.ACCOUNTING,
    ROUTES.SCHOOL_ASSIGNMENT_CHANGE,
    ROUTES.SCHOOL_AND_CLASSES,
    ROUTES.USERS
]

const pageList = computed(() => {

    let result = []
    const routes = router.getRoutes()

    pageOrder.forEach(i => {
        let routeMatch = routes.find(route => route.name === i)
        if (routeMatch) {
            result.push(routeMatch)
        }
        else {
            console.error('Could not find route', i)
        }
    })
    return result
})


const corporations = computed(() => {
    let result = []
    if (hasAdminRole.value || corporationScopes.value?.includes(CORPORATION_SCOPE_TYPE.SCHOOLS)) {
        result = [ ...result, ...userSchools.value ]
    }
    if (hasAdminRole.value || corporationScopes.value?.includes(CORPORATION_SCOPE_TYPE.SCHOOL_AUTHORITIES)) {
        result = [ ...result, ...userSchoolAuthorities.value ]
    }
    return result
})

const { sorted: sortedCorporations } = useSortedList(corporations, 'name')

const corporation = computed(() => {
    return corporations.value?.find(c => c.uuid === session.corporation.uuid)
})

const schoolYear = computed(() => {
    return schoolYears.value.find(schoolYear => schoolYear.uuid === session.schoolYear?.uuid)
})


const showRoute = (routeMeta) => {
    return isRouteAllowed(routeMeta.rights) && isRouteAllowedForCorporationScope(routeMeta.allowedCorporationScopes, corporationScope.value)
}

const isRouteAllowed = (listOfRights) => {
    if (!listOfRights) return true
    return !!listOfRights.find((right) => hasRole(right))
}

const isRouteAllowedForCorporationScope = (allowedCorporationScopes, corporationScope) => {
    return !!( allowedCorporationScopes?.includes(corporationScope) )
}

const isRouteAllowedForCorporationType = (allowedCorporationScopes, corporationType) => {
    switch (corporationType) {
        case CORPORATION_TYPE.SCHOOL: return isRouteAllowedForCorporationScope(allowedCorporationScopes, CORPORATION_SCOPE_TYPE.SCHOOLS)
        case CORPORATION_TYPE.SCHOOL_AUTHORITY: return isRouteAllowedForCorporationScope(allowedCorporationScopes, CORPORATION_SCOPE_TYPE.SCHOOL_AUTHORITIES)
        default: return false
    }
}

const switchCorporation = async (selectedCorporation) => {
    if (!selectedCorporation) {
        return
    }
    if (!isRouteAllowedForCorporationType(route?.value?.meta?.allowedCorporationScopes, selectedCorporation.corporationType)) {
        await goToHome()
    }
    else {
        await goToBase()
    }
    setCorporationInSession(selectedCorporation)
}

const setCorporationInSession = (selectedCorporation) => {
    session.corporation = {
        uuid: selectedCorporation.uuid,
        name: selectedCorporation.name,
        number: selectedCorporation.corporationNumber,
        corporationType: selectedCorporation.corporationType
    }
}

const switchSchoolYear = async (selectedSchoolYear) => {
    setSchoolYearInSession(selectedSchoolYear)
    await goToBase()
}

const setSchoolYearInSession = (selectedSchoolYear) => {
    session.schoolYear = JSON.parse(JSON.stringify(selectedSchoolYear))
}

const goToHome = async () => {
    session.uuidTicketholder = undefined
    return router.push({ name: ROUTES.HOME })
}

const goToBase = async () => {
    session.uuidTicketholder = undefined
    if (route.value?.meta?.basePath) {
        return router.push(route.value.meta.basePath)
    }
}

const getCorporationMdiIcon = (corporation) => {
    switch (corporation?.corporationType) {
        case CORPORATION_TYPE.SCHOOL: return 'mdi-school'
        case CORPORATION_TYPE.SCHOOL_AUTHORITY: return 'mdi-bank'
        default: return undefined
    }
}

const checkCorporationType = (corporationType, corporationScopes) => {
    switch (corporationType) {
        case CORPORATION_TYPE.SCHOOL:
            if (corporationScopes.includes(CORPORATION_SCOPE_TYPE.SCHOOLS)) {
                return true
            }
            snackState.showErrorSnack('Die ausgewählte Körperschaft passt nicht zu den Ihnen zugewiesenen Gruppen. Bitte wenden Sie sich an einen Administrator.')
            return false
        case CORPORATION_TYPE.SCHOOL_AUTHORITY:
            if (corporationScopes.includes(CORPORATION_SCOPE_TYPE.SCHOOL_AUTHORITIES)) {
                return true
            }
            snackState.showErrorSnack('Die ausgewählte Körperschaft passt nicht zu den Ihnen zugewiesenen Gruppen. Bitte wenden Sie sich an einen Administrator.')
            return false
        default: {
            snackState.showErrorSnack('Unbekannter Körperschaftskontext')
            return false
        }
    }
}

watchEffect(() => {
    checkCorporationType(session.corporation.corporationType, corporationScopes.value)
})

watchEffect(() => errorMessageSchools.value && snackState.showErrorSnack(errorMessageSchools.value))
watchEffect(() => errorMessageSchoolAuthorities.value && snackState.showErrorSnack(errorMessageSchoolAuthorities.value))
watch(currentUserErrorMessage, (errorMessage) => { errorMessage && snackState.showErrorSnack(errorMessage) })
</script>